import { EntityTypes } from "../../EntityTypes";
import { ITrainingActionResource, TrainingActionType } from "../../training-actions";
import { ITrainingTemplateResource } from "../../training-templates";
import { createApiMutationHook, IApiMutationFunction } from "../../utils/api";
import { mapRelationalValue, mapRelationalValueAsIRI, RelationalValue } from "../../utils/filters";

export interface ITrainingActionCreateMutationParameters {
    trainingTemplate: RelationalValue<ITrainingTemplateResource>;
    name: TrainingActionType;
    calendarDuration: number;
    calendarUnit?: "month" | "day" | "hour";
    pedagogicalHours?: number;
    description?: string;
    rank?: number;
}

export type ITrainingActionCreateMutationResult = ITrainingActionResource;

export const trainingActionCreateMutation: IApiMutationFunction<ITrainingActionCreateMutationParameters> = ({
    trainingTemplate,
    name,
    calendarDuration,
    calendarUnit = "month",
    pedagogicalHours = 1,
    description = "",
    rank = 0,
}) => {
    return {
        method: "POST",
        url: "/training_actions",
        data: {
            trainingTemplate: mapRelationalValueAsIRI(EntityTypes.TRAINING_TEMPLATE, trainingTemplate),
            name,
            calendarDuration,
            calendarUnit,
            pedagogicalHours,
            description,
            rank,
        },
    };
};

export const useTrainingActionCreateMutation = createApiMutationHook<
    ITrainingActionCreateMutationParameters,
    ITrainingActionCreateMutationResult
>(trainingActionCreateMutation);

export interface ITrainingActionsReorderMutationParameters {
    template: RelationalValue<ITrainingTemplateResource>;
    trainingActions: ITrainingActionResource[];
}

export type ITrainingActionsReorderMutationResult = ITrainingTemplateResource;

/** PATCH template with actions in new order. Send IRIs only so the backend processor rebuilds the collection in order without triggering orphanRemoval on trainingPhases. */
export const trainingActionsReorderMutation: IApiMutationFunction<ITrainingActionsReorderMutationParameters> = ({
    template,
    trainingActions,
}) => ({
    method: "PATCH",
    url: `/training_templates/${mapRelationalValue(template)}`,
    headers: {
        "Content-Type": "application/merge-patch+json",
    },
    data: {
        trainingActions: trainingActions.map(action => action["@id"]),
    },
});

export const useTrainingActionsReorderMutation = createApiMutationHook<
    ITrainingActionsReorderMutationParameters,
    ITrainingActionsReorderMutationResult
>(trainingActionsReorderMutation);

// Edit individual training action
export interface ITrainingActionUpdateMutationParameters {
    trainingAction: RelationalValue<ITrainingActionResource>;
    type?: TrainingActionType;
    description?: string;
    calendarDuration?: number;
    calendarUnit?: "month" | "day" | "hour";
    pedagogicalHours?: number;
    rank?: number;
}

export type ITrainingActionUpdateMutationResult = ITrainingActionResource;

export const trainingActionUpdateMutation: IApiMutationFunction<ITrainingActionUpdateMutationParameters> = ({
    trainingAction,
    type,
    description,
    calendarDuration,
    calendarUnit,
    pedagogicalHours,
    rank,
}) => {
    return {
        method: "PATCH",
        url: `/training_actions/${mapRelationalValue(trainingAction)}`,
        headers: {
            "Content-Type": "application/merge-patch+json",
        },
        data: {
            ...(type !== undefined && { type }),
            ...(description !== undefined && { description }),
            ...(calendarDuration !== undefined && { calendarDuration }),
            ...(calendarUnit !== undefined && { calendarUnit }),
            ...(pedagogicalHours !== undefined && { pedagogicalHours }),
            ...(rank !== undefined && { rank }),
        },
    };
};

export const useTrainingActionUpdateMutation = createApiMutationHook<
    ITrainingActionUpdateMutationParameters,
    ITrainingActionUpdateMutationResult
>(trainingActionUpdateMutation);

// Delete training action
export interface ITrainingActionDeleteMutationParameters {
    trainingAction: RelationalValue<ITrainingActionResource>;
}

export type ITrainingActionDeleteMutationResult = void;

export const trainingActionDeleteMutation: IApiMutationFunction<ITrainingActionDeleteMutationParameters> = ({
    trainingAction,
}) => {
    return {
        method: "DELETE",
        url: `/training_actions/${mapRelationalValue(trainingAction)}`,
    };
};

export const useTrainingActionDeleteMutation = createApiMutationHook<
    ITrainingActionDeleteMutationParameters,
    ITrainingActionDeleteMutationResult
>(trainingActionDeleteMutation);
